// src/services/FirestoreServices.js
import {
  collection,
  addDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  doc,
  setDoc,
  getDoc,
  where,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";

/**
 * getFolders
 * Fetches all folder documents for a given user with their subfolders.
 */
export async function getFolders(userId) {
  try {
    // Get all folders for this user
    const foldersRef = collection(db, "users", userId, "folders");
    const q = query(foldersRef, orderBy("createdAt", "desc"));
    const folderSnapshot = await getDocs(q);

    // For each folder, fetch its subfolders
    const foldersWithSubfolders = await Promise.all(
      folderSnapshot.docs.map(async (folderDoc) => {
        const folderId = folderDoc.id;
        const folderData = folderDoc.data();

        // Get subfolders collection for this folder
        const subfoldersRef = collection(
          db,
          "users",
          userId,
          "folders",
          folderId,
          "subfolders"
        );
        const subfoldersSnapshot = await getDocs(subfoldersRef);

        // Map subfolders
        const subfolders = subfoldersSnapshot.docs.map((subfolder) => ({
          id: subfolder.id,
          ...subfolder.data(),
          files: [], // Initialize empty files array
        }));

        // Return folder with subfolders
        return {
          id: folderId,
          ...folderData,
          subfolders,
        };
      })
    );

    return foldersWithSubfolders;
  } catch (error) {
    console.error("Error fetching folders:", error);
    throw error;
  }
}

/**
 * createFolder
 * Creates a new folder document with numbered subfolders as separate documents,
 * and an accreditation_folder document with its own subfolders.
 */

//Cinacall to sa folderUtils.js, used for creating folders sa folderDrawer.js
export async function createFolder(userId, folderName, subfolderNames) {
  try {
    // 1. Create the main folder document
    const folderRef = collection(db, "users", userId, "folders");
    const newFolderData = {
      folderName,
      createdAt: serverTimestamp(),
      userId,
      status: "draft", // default status
      adminNotes: "",
    };

    const folderDocRef = await addDoc(folderRef, newFolderData);
    const folderId = folderDocRef.id;

    // 2. Create subfolders as separate documents in a subcollection
    const subfoldersCollection = collection(
      db,
      "users",
      userId,
      "folders",
      folderId,
      "subfolders"
    );

    const createdSubfolders = await Promise.all(
      subfolderNames.map(async (name, index) => {
        const subfolderDoc = doc(subfoldersCollection, String(index));
        await setDoc(subfolderDoc, {
          subfolderName: name,
          createdAt: serverTimestamp(),
        });

        return {
          id: String(index),
          subfolderName: name,
          files: [],
        };
      })
    );

    // 3. Create accreditation_folder doc with a fixed ID, e.g. "main" or "root"
    const accreditationFolderDocRef = doc(
      db,
      "users",
      userId,
      "folders",
      folderId,
      "accreditation_folder",
      "main" // fixed ID for simplicity
    );
    await setDoc(accreditationFolderDocRef, {
      createdAt: serverTimestamp(),
      folderId: folderId,
      folderName: newFolderData.folderName,
    });

    // 4. Create placeholder docs in single_page and multi_page to initialize subcollections
    const singlePageDummyRef = doc(
      db,
      "users",
      userId,
      "folders",
      folderId,
      "accreditation_folder",
      "main",
      "single_page",
      "init"
    );
    const multiPageDummyRef = doc(
      db,
      "users",
      userId,
      "folders",
      folderId,
      "accreditation_folder",
      "main",
      "multi_page",
      "init"
    );
    await setDoc(singlePageDummyRef, { initialized: true });
    await setDoc(multiPageDummyRef, { initialized: true });

    // 5. Return info
    return {
      id: folderId,
      ...newFolderData,
      subfolders: createdSubfolders,
      createdAt: new Date(),
      accreditationFolderId: "main",
    };
  } catch (error) {
    console.error("Error creating folder:", error);
    throw error;
  }
}
